/* eslint-disable @typescript-eslint/brace-style */
/* eslint-disable @typescript-eslint/no-misused-promises */
import CustomTooltip from '../../../../components/CustomTooltip'
import React from 'react'
import Box from '@mui/material/Box'
import { FormProvider, useForm, type SubmitHandler, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import ValidationAlert from '../../../../components/ValidationAlert'
import { bindSetError, showServerFieldErrors } from '../../../../utils/showServerFieldErrors'
import _ from 'lodash'
import FormInput from '../../../../components/FormElements/FormInput'
import DefaultModalActions from '../../../../components/GenericModal/DefaultModalActions'
import FreeSoloMultiChips from '../../../../components/FormElements/FreeSoloMultiChips'
import { useMutation } from '@tanstack/react-query'
import api from '../../../../utils/api'
import { ATTRIBUTE_TYPES, READONLY_TOOLTIP } from '../../../../consts'
import { type IAttribute } from '../../../../models/Attributes'
import { notification } from '../../../../components/Notifications'
import { FormControlLabel, Switch } from '@mui/material'
import { LastUpdateMeta } from '../../../../components/GenericModal/ChangeMetaInfo'
import { useAccess } from '../../../../hooks/useAccess'
import ReportEnabledToggle from '../../ReportEnabledToggle'

interface FormType {
  attributeKey: string
  attributeType: string
  attributeValues: string[]
  isUserAttributes?: boolean
  isReportEnabled?: boolean
}

interface CreateAndEditStudentAttributesProps {
  selectedRow?: IAttribute | null
  selectedRowWithActions?: any
  handleCloseModal?: () => void
  refetch: () => void
}

export const CreateAndEditStudentAttributes: React.FC<CreateAndEditStudentAttributesProps> = ({ refetch, selectedRow, selectedRowWithActions, handleCloseModal = () => { } }): JSX.Element => {
  const { readonly } = useAccess(selectedRowWithActions?.actions ?? [])
  const [errors, setErrors] = React.useState<unknown>({})
  const isEditAction = selectedRow !== undefined

  const { attributeKey, attributeValues, attributeType, isReportEnabled } = selectedRow ?? {}

  const termFormSchema = yup.object().shape({
    attributeKey: yup.string().required('Title is required'),
    attributeValues: yup.array().of(yup.string())
  })

  const methods = useForm<any>({
    resolver: yupResolver(termFormSchema),
    defaultValues: {
      attributeKey,
      attributeValues,
      isUserAttributes: attributeType === ATTRIBUTE_TYPES.USER_COURSE_ATTRIBUTES,
      // Backwards compat: only an explicit false is "off". On edit, a legacy
      // null/undefined reads as on (enabled = value !== false); new attributes
      // default off.
      isReportEnabled: isEditAction ? isReportEnabled !== false : false
    }
  })
  const { handleSubmit, control, formState: { isDirty } } = methods

  const mutation = useMutation({
    meta: { silent: true },
    mutationFn: async (data: FormType) => {
      const payload = {
        attributeKey: data.attributeKey,
        attributeValues: data.attributeValues,
        attributeType: data.isUserAttributes ? ATTRIBUTE_TYPES.USER_COURSE_ATTRIBUTES : ATTRIBUTE_TYPES.STUDENT_COURSE_ATTRIBUTES,
        isReportEnabled: !!data.isReportEnabled
      }

      if (isEditAction) {
        const response = await api.patch(`/api/attributes/${selectedRow?.attributeId}`, payload)
        return response.data
      } else {
        const response = await api.post('/api/attributes/', payload)
        return response.data
      }
    },
    onSuccess: (res: any) => {
      const message = isEditAction ? 'Successfully updated' : 'Successfully created'
      notification(message, { variant: 'success', subTitle: res?.attributeKey })
    },
    onError: (error: unknown) => {
      setErrors(showServerFieldErrors(error, bindSetError(methods.setError), methods.clearErrors))
    }
  })

  const onSubmit: SubmitHandler<FormType> = async (data) => {
    try {
      mutation.mutate(data, {
        onSuccess: () => {
          handleCloseModal()
          refetch()
        },
        onError: (error) => {
          console.error('Error during submit:', error)
        }
      })
    } catch (error) {
      console.error('Error during submit:', error)
    }
  }

  return (
    <FormProvider {...methods}>
      <ValidationAlert errors={errors} />
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box display='flex' flexDirection='column' gap={6}>
          <FormInput
            name="attributeKey"
            label="Title"
            placeholder='Attribute title'
            control={control}
            required
          />
          <FreeSoloMultiChips placeholder='Attribute values' name='attributeValues' label='Values' />
        </Box>
        <Box marginLeft={4}>
          <Controller
            name="isUserAttributes"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <CustomTooltip title="this switch applies to all users including faculty, students, and admins.">
                    <Switch
                      checked={!!field.value}
                      onChange={e => { field.onChange(e.target.checked) }}
                      color="primary"
                    />
                  </CustomTooltip>
                }
                label="All User Attribute"
              />
            )}
          />
        </Box>
        <ReportEnabledToggle />

        <DefaultModalActions
          destructiveButtonProps={{
            onClick: handleCloseModal
          }}
          confirmButtonProps={{
            text: isEditAction ? 'Save' : 'Create',
            loading: mutation.isPending,
            type: 'submit',
            disabled: readonly || (isEditAction && !isDirty),
            tooltip: readonly ? READONLY_TOOLTIP : undefined
          }}
          lastUpdateMeta={isEditAction && (
            <LastUpdateMeta lastEditedBy={selectedRow?.modifiedByUsername} lastEditedAt={selectedRow?.modifiedOn} />
          )}

        />
      </form>
    </FormProvider>
  )
}
